<template>
  <div class="customer-self-help">
    <!-- 顶部标题 -->
    <div class="page-header">
      <h1 class="page-title">客户自解详情</h1>
    </div>

    <!-- 基础信息 -->
    <div class="basic-info">
      <div class="info-row">
        <span class="info-label">订单编号：</span>
        <span class="info-value order-no">{{ orderInfo.orderNo }}</span>
      </div>
      <div class="info-row">
        <span class="info-label">服务项目：</span>
        <span class="info-value">{{ orderInfo.serviceItem }}</span>
      </div>
      <div class="info-row">
        <span class="info-label">车牌号码：</span>
        <span class="info-value">{{ orderInfo.plateNumber }}</span>
      </div>
    </div>

    <!-- 服务过程照 -->
    <div
      class="photo-section"
      v-if="isEditable"
    >
      <div class="section-header">服务过程照</div>
      <div class="photo-list">
        <div
          v-for="(group, groupIndex) in servicePhotoTemplate"
          :key="group.primaryCode"
          class="photo-group"
        >
          <div
            v-for="(item, itemIndex) in group.secondary"
            :key="item.categoryCode"
            class="photo-item"
          >
            <div class="photo-label">
              <span class="photo-name">{{ item.typeName }}</span>
              <span
                v-if="item.requiredNum === 1"
                class="required-text"
              >（必传）</span>
            </div>
            <div class="photo-upload-row">
              <div
                v-for="(photo, photoIndex) in item.mediaList"
                :key="photoIndex"
                class="photo-preview-wrapper"
              >
                <van-image
                  :src="photo.url"
                  fit="cover"
                  class="uploaded-photo"
                  @click="previewImage(item.mediaList, photoIndex)"
                />
                <van-icon
                  name="clear"
                  class="delete-icon"
                  @click.stop="deleteServicePhoto(groupIndex, itemIndex, photoIndex, photo)"
                />
              </div>
              <div
                class="photo-upload-box"
                @click="handleUploadServicePhoto(groupIndex, itemIndex, item)"
                v-if="!item.mediaList || item.mediaList.length < item.maxNum"
              >
                <img
                  src="@/assets/images/icon-camera.png"
                  class="upload-icon-img"
                  alt="添加照片"
                >
                <span class="upload-text">添加照片</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 只读状态下的服务过程照展示 -->
    <div
      class="photo-section readonly"
      v-else
    >
      <div class="section-header">服务过程照</div>
      <div class="photo-list">
        <div
          v-for="(group) in servicePhotoTemplate"
          :key="group.primaryCode"
          class="photo-group"
        >
          <div
            v-for="(item) in group.secondary"
            :key="item.categoryCode"
            class="photo-item readonly"
          >
            <div class="photo-label">
              <span class="photo-name">{{ item.typeName }}</span>
              <span
                v-if="item.requiredNum === 1"
                class="required-text"
              >（必传）</span>
            </div>
            <div
              class="photo-row"
              v-if="item.mediaList && item.mediaList.length > 0"
            >
              <van-image
                v-for="(photo, photoIndex) in item.mediaList"
                :key="photoIndex"
                :src="photo.url"
                fit="cover"
                class="readonly-photo"
                @click="previewImage(item.mediaList, photoIndex)"
              />
            </div>
            <div
              v-else
              class="no-photo"
            >暂无照片</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 报销支付凭证 -->
    <div
      class="reimbursement-section"
      v-if="isEditable"
    >
      <div class="section-header">报销支付凭证</div>
      <div class="form-section">
        <div class="form-item">
          <span class="form-label">银行卡户名</span>
          <van-field
            v-model="formData.bankAccountName"
            placeholder="请填写"
            maxlength="20"
            :disabled="!isEditable"
            @blur="saveBankInfo"
          />
        </div>
        <div class="form-item">
          <span class="form-label">银行卡号</span>
          <van-field
            v-model="formData.bankCardNo"
            placeholder="请填写"
            maxlength="20"
            :disabled="!isEditable"
            @input="validateBankCardNo"
            @blur="saveBankInfo"
          />
        </div>
      </div>

      <!-- 实际支付凭证 -->
      <div class="photo-item">
        <div class="photo-label">
          <span class="photo-name">实际支付凭证</span>
          <span class="required-text">（必传）</span>
        </div>
        <div class="photo-upload-row">
          <div
            v-for="(photo, index) in paymentVouchers"
            :key="index"
            class="photo-preview-wrapper"
          >
            <van-image
              :src="photo.url"
              fit="cover"
              class="uploaded-photo"
              @click="previewImage(paymentVouchers, index)"
            />
            <van-icon
              name="clear"
              class="delete-icon"
              @click.stop="deletePaymentVoucher(index)"
            />
          </div>
          <div
            class="photo-upload-box"
            @click="handleUploadPaymentVoucher"
            v-if="paymentVouchers.length < 3"
          >
            <img
              src="@/assets/images/icon-camera.png"
              class="upload-icon-img"
              alt="添加照片"
            >
            <span class="upload-text">添加照片</span>
          </div>
        </div>
      </div>

      <!-- 收款银行卡正面照 -->
      <div class="photo-item">
        <div class="photo-label">
          <span class="photo-name">收款银行卡正面照</span>
          <span class="required-text">（必传）</span>
        </div>
        <div class="photo-upload-row">
          <div
            v-for="(photo, index) in bankCardPhotos"
            :key="index"
            class="photo-preview-wrapper"
          >
            <van-image
              :src="photo.url"
              fit="cover"
              class="uploaded-photo"
              @click="previewImage(bankCardPhotos, index)"
            />
            <van-icon
              name="clear"
              class="delete-icon"
              @click.stop="deleteBankCardPhoto(index)"
            />
          </div>
          <div
            class="photo-upload-box"
            @click="handleUploadBankCardPhoto"
            v-if="bankCardPhotos.length < 3"
          >
            <img
              src="@/assets/images/icon-camera.png"
              class="upload-icon-img"
              alt="添加照片"
            >
            <span class="upload-text">添加照片</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 只读状态下的报销支付凭证展示 -->
    <div
      class="reimbursement-section readonly"
      v-else
    >
      <div class="section-header">报销支付凭证</div>
      <div class="readonly-info">
        <div class="info-item">
          <span class="label">银行卡户名</span>
          <span class="value">{{ formData.bankAccountName || '-' }}</span>
        </div>
        <div class="info-item">
          <span class="label">银行卡号</span>
          <span class="value">{{ formData.bankCardNo || '-' }}</span>
        </div>
      </div>

      <div class="photo-item readonly">
        <div class="photo-label">
          <span class="photo-name">实际支付凭证</span>
          <span class="required-text">（必传）</span>
        </div>
        <div
          class="photo-row"
          v-if="paymentVouchers.length > 0"
        >
          <van-image
            v-for="(photo, index) in paymentVouchers"
            :key="index"
            :src="photo.url"
            fit="cover"
            class="readonly-photo"
            @click="previewImage(paymentVouchers, index)"
          />
        </div>
        <div
          v-else
          class="no-photo"
        >暂无照片</div>
      </div>

      <div class="photo-item readonly">
        <div class="photo-label">
          <span class="photo-name">收款银行卡正面照</span>
        </div>
        <div
          class="photo-row"
          v-if="bankCardPhotos.length > 0"
        >
          <van-image
            v-for="(photo, index) in bankCardPhotos"
            :key="index"
            :src="photo.url"
            fit="cover"
            class="readonly-photo"
            @click="previewImage(bankCardPhotos, index)"
          />
        </div>
        <div
          v-else
          class="no-photo"
        >暂无照片</div>
      </div>
    </div>

    <!-- 底部操作栏 -->
    <div
      class="footer"
      v-if="isEditable"
    >
      <div
        class="contact-service"
        @click="contactCustomerService"
      >
        <img
          src="@/assets/images/icon-service.png"
          class="service-icon-img"
          alt="联系客服"
        >
        <span class="service-text">联系客服</span>
      </div>
      <div
        class="submit-btn"
        :class="{ disabled: !canSubmit }"
        @click="handleSubmit"
      >
        <span>提交报销审核</span>
      </div>
    </div>

    <!-- 提交成功提示 -->
    <van-dialog
      v-model="showSuccessDialog"
      title="提交成功"
      message="您的申请已提交，请等待客服审核后为您报销打款"
      confirm-button-text="确定"
      @confirm="handleDialogConfirm"
    />
  </div>
</template>

<script>
import { ImagePreview, Toast } from 'vant';
import uploadHelper from '@/common/upload-helper';
import {
  selfSolveTaskDetail,
  selfSolveMediaTemplate,
  selfSolveMediaDetail,
  selfSolveSaveMedia,
  selfSolveDelMedia,
  selfSolveAudit
} from '@/common/api.js';
import Compressor from 'compressorjs';
import dayjs from 'dayjs';
import { getCurrentPosition } from '@/views/generalOrder/common/getCurrentPosition';

export default {
  name: 'CustomerSelfHelp',
  data() {
    return {
      // 路由参数
      orderNo: '',
      taskNo: '',
      // 订单信息
      orderInfo: {
        orderNo: '',
        serviceItem: '',
        plateNumber: '',
        status: '',
        categoryCode: '',
        useTraceCar: 0,
        extraProduct: []
      },
      // 表单数据
      formData: {
        bankAccountName: '',
        bankCardNo: ''
      },
      // 服务过程照 - 从接口获取模板
      servicePhotoTemplate: [],
      // 实际支付凭证
      paymentVouchers: [],
      // 收款银行卡正面照
      bankCardPhotos: [],
      // 提交成功弹窗
      showSuccessDialog: false,
      // 当前位置信息
      currentPosition: null,
      // 报销支付凭证模板
      reimbursementTemplate: []
    };
  },
  computed: {
    // 是否可编辑
    isEditable() {
      return ['PROCESSING', 'AUDIT_RETURN'].includes(this.orderInfo.status);
    },
    // 是否可以提交
    canSubmit() {
      // 校验服务过程照必传项
      const servicePhotosValid = this.servicePhotoTemplate
        .flatMap(group => group.secondary || [])
        .filter(item => item.requiredNum === 1)
        .every(item => item.mediaList && item.mediaList.length > 0);

      // 校验银行卡户名
      const bankAccountNameValid = this.formData.bankAccountName && this.formData.bankAccountName.trim().length > 0;

      // 校验银行卡号或收款银行卡正面照至少有一个
      const bankInfoValid = (this.formData.bankCardNo && this.formData.bankCardNo.trim().length > 0) ||
        this.bankCardPhotos.length > 0;

      // 校验实际支付凭证必传
      const paymentVoucherValid = this.paymentVouchers.length > 0;

      return servicePhotosValid && bankAccountNameValid && bankInfoValid && paymentVoucherValid;
    }
  },
  created() {
    const { orderNo, taskNo } = this.$route.query;
    if (orderNo && taskNo) {
      this.orderNo = orderNo;
      this.taskNo = taskNo;
    }
  },
  mounted() {
    this.fetchOrderDetail();
  },
  methods: {
    // 获取当前位置
    async getCurrentPosition() {
      try {
        const position = await getCurrentPosition();
        this.currentPosition = position;
        return position;
      } catch (error) {
        console.error('获取位置失败:', error);
        Toast('当前位置获取失败，请确认是否打开位置权限！');
        return null;
      }
    },

    // 添加水印并压缩图片
    addWatermark(file, dayjsDate) {
      return new Promise((resolve, reject) => {
        const quality = 0.8;
        new Compressor(file, {
          strict: false,
          quality,
          maxWidth: 2160,
          minWidth: 1080,
          drew: (context, canvas) => {
            const fillWidth = canvas.width * 3 / 4;
            let fontSize = 30;
            if (canvas.width > 1080) {
              fontSize = fontSize * canvas.width / 1080;
            }
            const wordLength = Math.ceil(fillWidth / fontSize);
            const lineHeight = 1.5;
            const fillX = canvas.width - fontSize;
            context.font = `${fontSize}px Roboto, PingFang`;
            context.fillStyle = 'red';
            context.textAlign = 'right';
            context.textBaseline = 'middle';

            // 第一行：时间
            context.fillText(dayjsDate.format('YYYY-MM-DD HH:mm:ss'), fillX, fontSize * lineHeight);

            // 第二行及以后：地址（自动换行）
            if (this.currentPosition && this.currentPosition.formattedAddress) {
              const address = this.currentPosition.formattedAddress;
              const addressWordLength = address.length;
              const lines = Math.ceil(addressWordLength / wordLength);
              if (lines > 1) {
                for (let index = 0; index < lines; index++) {
                  const addressLine = address.slice(wordLength * index, wordLength * (index + 1));
                  context.fillText(addressLine, fillX, fontSize * lineHeight * (2 + index));
                }
              } else {
                context.fillText(address, fillX, fontSize * lineHeight * 2);
              }

              // 最后一行：经纬度
              const longitude = this.currentPosition.longitude || '';
              const latitude = this.currentPosition.latitude || '';
              context.fillText(`经度:${longitude} 纬度:${latitude}`, fillX, fontSize * lineHeight * (2 + lines));
            }
          },
          success: (result) => {
            resolve(result);
          },
          error: (err) => {
            console.log(err.message);
            reject(err);
          }
        });
      });
    },

    // 获取订单详情
    async fetchOrderDetail() {
      if (!(this.orderNo && this.taskNo)) {
        return;
      }
      Toast.loading({ message: '加载中...', forbidClick: true });
      try {
        const params = { orderNo: this.orderNo, taskNo: this.taskNo };
        const data = await selfSolveTaskDetail(params);
        if (data) {
          this.orderInfo = {
            ...this.orderInfo,
            orderNo: data.orderNo,
            serviceItem: data.productSkuName,
            plateNumber: data.carNo,
            status: data.orderStatus,
            categoryCode: data.categoryCode,
            useTraceCar: data.useTraceCar || 0,
            extraProduct: data.extraProduct || []
          };
        }
        await this.fetchServicePhotoTemplate();
        await this.fetchServicePhotos();
        await this.fetchReimbursementTemplate();
      } catch (error) {
        console.error('获取订单详情失败:', error);
      } finally {
        Toast.clear();
      }
    },

    // 获取服务凭证参数
    getPhotoParams() {
      const params = {};
      params.useTraceCar = this.orderInfo.useTraceCar || 0;
      params.pictureTypes = ['009'];

      const { extraProduct } = this.orderInfo;
      if (extraProduct && extraProduct.length > 0) {
        const trainingWheels = extraProduct.find(item => item.desc === '00001');
        if (trainingWheels) {
          params.pictureTypes.push(trainingWheels.skuValueCode || '012');
        }
        const shopDrawing = extraProduct.find(item => item.desc === '00002');
        if (shopDrawing) {
          params.pictureTypes.push(shopDrawing.skuValueCode || '013');
        }
      }

      return params;
    },

    // 获取服务凭证模板
    async fetchServicePhotoTemplate() {
      try {
        const photoParams = this.getPhotoParams();
        const data = await selfSolveMediaTemplate({
          orderNo: this.orderNo,
          useTraceCar: photoParams.useTraceCar,
          pictureTypes: photoParams.pictureTypes,
          requestType: photoParams.pictureTypes.length > 0 ? 2 : 1
        });
        if (data) {
          this.servicePhotoTemplate = data
            .map(group => ({
              ...group,
              secondary: (group.secondary || [])
                .filter(item => item.requiredNum === 1)
                .map(item => ({
                  ...item,
                  mediaList: []
                }))
            }))
            .filter(group => group.secondary.length > 0);
        }
      } catch (error) {
        console.error('获取服务凭证模板失败:', error);
      }
    },

    // 获取已上传的服务过程照
    async fetchServicePhotos() {
      try {
        const data = await selfSolveMediaDetail({
          orderNo: this.orderNo,
          requestType: 1
        });
        if (data && this.servicePhotoTemplate.length > 0) {
          this.servicePhotoTemplate.forEach(group => {
            group.secondary.forEach(item => {
              const mediaList = data.filter(media => media.categoryCode === item.categoryCode);
              // 始终更新 mediaList，如果没有数据则设为空数组
              this.$set(item, 'mediaList', mediaList.map(media => ({
                url: media.accessUrl || media.url,
                picId: media.picId,
                id: media.id
              })));
            });
          });
        }
      } catch (error) {
        console.error('获取服务过程照失败:', error);
      }
    },

    // 联系客服
    contactCustomerService() {
      window.location.href = 'tel:4000026862';
    },

    // 校验银行卡号输入
    validateBankCardNo(value) {
      this.formData.bankCardNo = value.replace(/\D/g, '');
    },

    // 上传服务过程照（带水印+实时保存）
    async handleUploadServicePhoto(groupIndex, itemIndex, item) {
      try {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.capture = 'camera'; // 强制使用相机
        input.onchange = async(e) => {
          const file = e.target.files[0];
          if (!file) return;

          // 验证文件类型
          const fileExt = file.name.replace(/.+\./, '').toLowerCase();
          if (!['jpg', 'jpeg', 'png'].includes(fileExt)) {
            Toast('请上传 jpg、jpeg、png 格式的图片');
            return;
          }
          // 验证文件大小
          if (file.size / 1024 / 1024 > 10) {
            Toast('上传文件大小不能超过 10MB');
            return;
          }

          Toast.loading({ message: '上传中...', forbidClick: true });

          try {
            // 获取当前位置
            const position = await this.getCurrentPosition();
            if (!position) {
              return;
            }

            // 添加水印并压缩
            const today = dayjs();
            const compressedFile = await this.addWatermark(file, today);
            // 上传图片
            const res = await uploadHelper(compressedFile);

            if (res && res.accessUrl) {
              // 实时保存到服务器
              const today = dayjs();
              const media = {
                pictureType: item.primaryCode,
                categoryCode: item.categoryCode,
                fileName: file.name,
                fileType: 'image',
                orderNo: this.orderNo,
                uploadFrom: 'H5上传',
                fileId: res.picId,
                albumNoMarkUpload: true,
                // 水印信息
                watermarkTime: today.format('YYYY-MM-DD HH:mm:ss'),
                watermarkLongitude: this.currentPosition?.longitude || '',
                watermarkLatitude: this.currentPosition?.latitude || '',
                watermarkAddress: this.currentPosition?.formattedAddress || '',
                // 拍摄位置信息
                takeLongitude: this.currentPosition?.longitude || '',
                takeLatitude: this.currentPosition?.latitude || '',
                takeLocaltion: this.currentPosition?.formattedAddress || ''
              };

              await selfSolveSaveMedia({ medias: [media] });

              // 重新获取媒体列表，获取正确的 id
              await this.fetchServicePhotos();

              Toast.success('上传成功');
            } else {
              Toast.fail('上传失败');
            }
          } catch (error) {
            console.error('上传失败:', error);
            Toast.fail('上传失败');
          } finally {
            setTimeout(() => {
              Toast.clear();
            }, 2000);
          }
        };
        input.click();
      } catch (error) {
        Toast.fail('上传失败');
        console.error(error);
      }
    },

    // 删除服务过程照（实时删除）
    async deleteServicePhoto(groupIndex, itemIndex, photoIndex, photo) {
      try {
        await this.$dialog.confirm({
          title: '提示',
          message: '确定删除该图片吗?'
        });

        Toast.loading({ message: '删除中...', forbidClick: true });

        // 调用删除接口 - 优先使用 mediaId，其次是 id 或 picId
        const mediaId = photo.mediaId || photo.id || photo.picId;
        if (!mediaId) {
          Toast.fail('图片ID不存在，无法删除');
          return;
        }
        await selfSolveDelMedia({
          orderNo: this.orderNo,
          mediaId: mediaId
        });

        // 更新本地数据
        this.servicePhotoTemplate[groupIndex].secondary[itemIndex].mediaList.splice(photoIndex, 1);

        Toast.clear();
        Toast.success('删除成功');
      } catch (error) {
        // 用户取消不报错
        if (error && error !== 'cancel' && !error.toString().includes('cancel')) {
          console.error('删除失败:', error);
          Toast.fail('删除失败');
        }
      }
    },

    // 上传实际支付凭证（实时上传，无水印）
    async handleUploadPaymentVoucher() {
      try {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async(e) => {
          const file = e.target.files[0];
          if (!file) return;

          // 验证文件类型
          const fileExt = file.name.replace(/.+\./, '').toLowerCase();
          if (!['jpg', 'jpeg', 'png'].includes(fileExt)) {
            Toast('请上传 jpg、jpeg、png 格式的图片');
            return;
          }
          // 验证文件大小
          if (file.size / 1024 / 1024 > 10) {
            Toast('上传文件大小不能超过 10MB');
            return;
          }

          Toast.loading({ message: '上传中...', forbidClick: true });

          try {
            // 直接上传，不添加水印
            const res = await uploadHelper(file);

            if (res && res.accessUrl) {
              // 实时保存到服务器
              const today = dayjs();
              const media = {
                pictureType: '015', // 实际支付凭证类型
                categoryCode: '015',
                fileName: file.name,
                fileType: 'image',
                orderNo: this.orderNo,
                uploadFrom: 'H5上传',
                fileId: res.picId,
                albumNoMarkUpload: true,
                watermarkTime: today.format('YYYY-MM-DD HH:mm:ss')
              };

              await selfSolveSaveMedia({ medias: [media] });

              // 重新获取媒体列表（只获取详情，不刷新模板）
              await this.fetchReimbursementPhotos();

              Toast.success('上传成功');
            } else {
              Toast.fail('上传失败');
            }
          } catch (error) {
            console.error('上传失败:', error);
            Toast.fail('上传失败');
          } finally {
            Toast.clear();
          }
        };
        input.click();
      } catch (error) {
        Toast.fail('上传失败');
        console.error(error);
      }
    },

    // 删除实际支付凭证（实时删除）
    async deletePaymentVoucher(index) {
      try {
        const photo = this.paymentVouchers[index];
        await this.$dialog.confirm({
          title: '提示',
          message: '确定删除该图片吗?'
        });

        Toast.loading({ message: '删除中...', forbidClick: true });

        // 调用删除接口
        const mediaId = photo.mediaId || photo.id || photo.picId;
        if (!mediaId) {
          Toast.fail('图片ID不存在，无法删除');
          return;
        }
        await selfSolveDelMedia({
          orderNo: this.orderNo,
          mediaId: mediaId
        });

        // 更新本地数据
        this.paymentVouchers.splice(index, 1);

        Toast.clear();
        Toast.success('删除成功');
      } catch (error) {
        // 用户取消不报错
        if (error && error !== 'cancel' && !error.toString().includes('cancel')) {
          console.error('删除失败:', error);
          Toast.fail('删除失败');
        }
      }
    },

    // 获取报销支付凭证模板和详情（页面初始化时调用）
    async fetchReimbursementTemplate() {
      try {
        // 获取模板
        const templateData = await selfSolveMediaTemplate({
          orderNo: this.orderNo,
          mediaTemplateType: 'userUpload',
          requestType: 1
        });
        if (templateData) {
          this.reimbursementTemplate = templateData.map(group => ({
            ...group,
            secondary: (group.secondary || []).map(item => ({
              ...item,
              mediaList: []
            }))
          }));
        }

        // 获取已上传的媒体详情
        await this.fetchReimbursementPhotos();
      } catch (error) {
        console.error('获取报销支付凭证失败:', error);
      }
    },

    // 获取报销支付凭证详情（上传/删除后调用）
    async fetchReimbursementPhotos() {
      try {
        const detailData = await selfSolveMediaDetail({
          orderNo: this.orderNo,
          mediaTemplateType: 'userUpload',
          requestType: 1
        });
        if (detailData && this.reimbursementTemplate.length > 0) {
          this.reimbursementTemplate.forEach(group => {
            group.secondary.forEach(item => {
              const mediaList = detailData.filter(media => media.categoryCode === item.categoryCode);
              this.$set(item, 'mediaList', mediaList.map(media => ({
                url: media.accessUrl || media.url,
                picId: media.picId,
                id: media.id
              })));
            });
          });
        }
      } catch (error) {
        console.error('获取报销支付凭证详情失败:', error);
      }
    },

    // 上传收款银行卡正面照（实时上传，无水印）
    async handleUploadBankCardPhoto() {
      try {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async(e) => {
          const file = e.target.files[0];
          if (!file) return;

          // 验证文件类型
          const fileExt = file.name.replace(/.+\./, '').toLowerCase();
          if (!['jpg', 'jpeg', 'png'].includes(fileExt)) {
            Toast('请上传 jpg、jpeg、png 格式的图片');
            return;
          }
          // 验证文件大小
          if (file.size / 1024 / 1024 > 10) {
            Toast('上传文件大小不能超过 10MB');
            return;
          }

          Toast.loading({ message: '上传中...', forbidClick: true });

          try {
            // 直接上传，不添加水印
            const res = await uploadHelper(file);

            if (res && res.accessUrl) {
              // 实时保存到服务器
              const today = dayjs();
              const media = {
                pictureType: '016', // 银行卡照片类型
                categoryCode: '016',
                fileName: file.name,
                fileType: 'image',
                orderNo: this.orderNo,
                uploadFrom: 'H5上传',
                fileId: res.picId,
                albumNoMarkUpload: true,
                watermarkTime: today.format('YYYY-MM-DD HH:mm:ss')
              };

              await selfSolveSaveMedia({ medias: [media] });

              // 重新获取媒体列表（只获取详情，不刷新模板）
              await this.fetchReimbursementPhotos();

              Toast.success('上传成功');
            } else {
              Toast.fail('上传失败');
            }
          } catch (error) {
            console.error('上传失败:', error);
            Toast.fail('上传失败');
          } finally {
            Toast.clear();
          }
        };
        input.click();
      } catch (error) {
        Toast.fail('上传失败');
        console.error(error);
      }
    },

    // 删除收款银行卡正面照（实时删除）
    async deleteBankCardPhoto(index) {
      try {
        const photo = this.bankCardPhotos[index];
        await this.$dialog.confirm({
          title: '提示',
          message: '确定删除该图片吗?'
        });

        Toast.loading({ message: '删除中...', forbidClick: true });

        // 调用删除接口
        const mediaId = photo.mediaId || photo.id || photo.picId;
        if (!mediaId) {
          Toast.fail('图片ID不存在，无法删除');
          return;
        }
        await selfSolveDelMedia({
          orderNo: this.orderNo,
          mediaId: mediaId
        });

        // 重新获取媒体列表（只获取详情，不刷新模板）
        await this.fetchReimbursementPhotos();

        Toast.clear();
        Toast.success('删除成功');
      } catch (error) {
        // 用户取消不报错
        if (error && error !== 'cancel' && !error.toString().includes('cancel')) {
          console.error('删除失败:', error);
          Toast.fail('删除失败');
        }
      }
    },

    // 保存银行卡信息
    async saveBankInfo() {
      // 实际项目中调用API保存
    },

    // 预览图片
    previewImage(images, index) {
      const urls = images.map(img => img.url);
      ImagePreview({
        images: urls,
        startPosition: index
      });
    },

    // 提交审核
    async handleSubmit() {
      if (!this.canSubmit) {
        const allRequiredPhotos = this.servicePhotoTemplate
          .flatMap(group => group.secondary || [])
          .filter(item => item.requiredNum === 1);

        const hasAllRequiredPhotos = allRequiredPhotos.every(item =>
          item.mediaList && item.mediaList.length > 0
        );

        if (!hasAllRequiredPhotos) {
          Toast('请上传所有必传的服务过程照');
          return;
        }
        if (!this.formData.bankAccountName) {
          Toast('请填写银行卡户名');
          return;
        }
        if (!this.formData.bankCardNo && this.bankCardPhotos.length === 0) {
          Toast('请填写银行卡号或上传收款银行卡正面照');
          return;
        }
        if (this.paymentVouchers.length === 0) {
          Toast('请上传实际支付凭证');
          return;
        }
        return;
      }

      try {
        Toast.loading({ message: '提交中...', forbidClick: true });
        await selfSolveAudit({
          orderNo: this.orderNo,
          taskNo: this.taskNo,
          selfSolveBankAccountName: this.formData.bankAccountName,
          selfSolveBankAccount: this.formData.bankCardNo
        });
        Toast.clear();
        this.showSuccessDialog = true;
      } catch (error) {
        Toast.fail('提交失败，请重试');
        console.error(error);
      }
    },

    // 弹窗确认
    handleDialogConfirm() {
      this.orderInfo.status = 'AUDITING';
      this.showSuccessDialog = false;
    }
  }
};
</script>

<style lang="scss" scoped>
// Design System Variables
$color-primary: #B9D4EE;
$color-primary-light: #F6F9FC;
$color-danger: #FF8A59;
$color-text-primary: #383838;
$color-text-secondary: #999999;
$color-text-tertiary: #C8C8C8;
$color-border: #E6E6E6;
$color-bg-page: #F5F5F5;
$color-bg-card: #FFFFFF;

$radius-sm: 4px;
$radius-md: 0.2rem;
$radius-lg: 0.48rem;

$space-1: 0.08rem;
$space-2: 0.16rem;
$space-3: 0.24rem;
$space-4: 0.32rem;
$space-5: 0.4rem;

.customer-self-help {
  min-height: 100vh;
  background: $color-bg-page;
  padding-bottom: 1.44rem;
}

// 页面头部
.page-header {
  background: $color-bg-card;
  padding: 0.32rem 0;
  text-align: center;

  .page-title {
    font-size: 0.36rem;
    font-weight: 500;
    color: $color-text-primary;
  }
}

// 基础信息卡片
.basic-info {
  background: $color-bg-card;
  padding: 0.32rem;
  margin: 0.24rem;
  border-radius: $radius-md;

  .info-row {
    display: flex;
    align-items: center;
    margin-bottom: 0.32rem;
    line-height: 0.45rem;

    &:last-child {
      margin-bottom: 0;
    }

    .info-label {
      font-size: 0.32rem;
      color: $color-text-secondary;
      min-width: 1.6rem;
    }

    .info-value {
      font-size: 0.32rem;
      color: $color-text-primary;

      &.order-no {
        font-weight: 600;
      }
    }
  }
}

// 服务过程照卡片
.photo-section {
  background: $color-bg-card;
  margin: 0.24rem;
  padding: 0.32rem;
  border-radius: $radius-md;

  .section-header {
    font-size: 0.32rem;
    font-weight: 600;
    color: $color-text-primary;
    margin-bottom: 0.32rem;
    line-height: 0.45rem;
  }

  .photo-list {
    .photo-group {
      margin-bottom: 0.32rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .photo-item {
      margin-bottom: 0.32rem;

      &:last-child {
        margin-bottom: 0;
      }

      .photo-label {
        display: flex;
        align-items: center;
        margin-bottom: 0.20rem;
        line-height: 0.45rem;

        .photo-name {
          font-size: 0.32rem;
          color: $color-text-primary;
        }

        .required-text {
          font-size: 0.32rem;
          color: $color-danger;
        }
      }

      .photo-upload-row {
        display: flex;
        flex-wrap: wrap;
        gap: 0.24rem;

        .photo-preview-wrapper {
          position: relative;
          width: 2.56rem;
          height: 1.76rem;
          border-radius: 0.12rem;

          .uploaded-photo {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 0.12rem;
            overflow: hidden;
          }

          .delete-icon {
            position: absolute;
            top: -0.08rem;
            right: -0.08rem;
            font-size: 0.32rem;
            color: $color-danger;
            background: #fff;
            border-radius: 50%;
          }
        }

        .photo-upload-box {
          width: 2.56rem;
          height: 1.76rem;
          border: 1px dashed $color-primary;
          border-radius: 0.12rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: $color-primary-light;
          cursor: pointer;

          .upload-icon-img {
            width: 0.48rem;
            height: 0.48rem;
            margin-bottom: 0.04rem;
          }

          .upload-text {
            font-size: 0.24rem;
            color: #3399FF;
          }
        }
      }

      .photo-row {
        display: flex;
        flex-wrap: wrap;
        gap: 0.16rem;

        .readonly-photo {
          width: 2.56rem;
          height: 1.76rem;
          border-radius: 0.12rem;
          overflow: hidden;
          object-fit: cover;
        }
      }

      .no-photo {
        font-size: 0.32rem;
        color: $color-text-tertiary;
        padding: 0.24rem 0;
      }
    }
  }
}

// 报销支付凭证卡片
.reimbursement-section {
  background: $color-bg-card;
  margin: 0.24rem;
  padding: 0.32rem 0.4rem;
  border-radius: $radius-md;

  .section-header {
    font-size: 0.32rem;
    font-weight: 600;
    color: $color-text-primary;
    margin-bottom: 0.32rem;
  }

  .form-section {
    margin-bottom: 0.32rem;

    .form-item {
      display: flex;
      align-items: center;
      padding: 0.3rem 0;
      border-bottom: 1px solid $color-border;

      .form-label {
        font-size: 0.32rem;
        color: $color-text-primary;
        width: 2.08rem;
      }

      .van-field {
        flex: 1;
        padding: 0;
        font-size: 0.32rem;
        color: $color-text-primary;
        font-weight: 600;

        &::after {
          display: none;
        }

        ::v-deep .van-field__control {
          font-size: 0.32rem;
          color: $color-text-primary;
          font-weight: 600;
          &::placeholder {
            color: $color-text-tertiary;
            font-weight: 400;
          }
        }
      }
    }
  }

  .photo-item {
    margin-bottom: 0.32rem;

    &:last-child {
      margin-bottom: 0;
    }

    .photo-label {
      display: flex;
      align-items: center;
      margin-bottom: 0.2rem;

      .photo-name {
        font-size: 0.32rem;
        color: $color-text-primary;
      }

      .required-text {
        font-size: 0.32rem;
        color: $color-danger;
      }
    }

    .photo-upload-row {
      display: flex;
      flex-wrap: wrap;
      gap: 0.24rem;

      .photo-preview-wrapper {
        position: relative;
        width: 2.56rem;
        height: 1.76rem;
        border-radius: 0.12rem;

        .uploaded-photo {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 0.12rem;
          overflow: hidden;
        }

        .delete-icon {
          position: absolute;
          top: -0.08rem;
          right: -0.08rem;
          font-size: 0.32rem;
          color: $color-danger;
          background: #fff;
          border-radius: 50%;
        }
      }

      .photo-upload-box {
        width: 2.56rem;
        height: 1.76rem;
        border: 1px dashed $color-primary;
        border-radius: 0.12rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: $color-primary-light;
        cursor: pointer;

        .upload-icon-img {
          width: 0.48rem;
          height: 0.48rem;
          margin-bottom: 0.04rem;
        }

        .upload-text {
          font-size: 0.24rem;
          color: #3399FF;
        }
      }
    }

    .photo-row {
      display: flex;
      flex-wrap: wrap;
      gap: 0.16rem;

      .readonly-photo {
        width: 2.56rem;
        height: 1.76rem;
        border-radius: 0.12rem;
        object-fit: cover;
        overflow: hidden;
      }
    }

    .no-photo {
      font-size: 0.32rem;
      color: $color-text-tertiary;
      padding: 0.24rem 0;
    }
  }

  &.readonly {
    .readonly-info {
      margin-bottom: 0.32rem;

      .info-item {
        display: flex;
        padding: 0.24rem 0;
        border-bottom: 1px solid $color-border;

        &:last-child {
          border-bottom: none;
        }

        .label {
          font-size: 0.32rem;
          color: $color-text-secondary;
          width: 2rem;
        }

        .value {
          font-size: 0.32rem;
          color: $color-text-primary;
          flex: 1;
        }
      }
    }
  }
}

// 底部操作栏
.footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: $color-bg-card;
  padding: 0.24rem 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.32rem;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);

  .contact-service {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 1.2rem;
    cursor: pointer;

    .service-icon-img {
      width: 0.64rem;
      height: 0.64rem;
      margin-bottom: 0.04rem;
    }

    .service-text {
      font-size: 0.24rem;
      color: $color-text-secondary;
    }
  }

  .submit-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #3399FF;
    color: #fff;
    height: 0.96rem;
    border-radius: $radius-lg;
    font-size: 0.32rem;
    font-weight: 500;
    cursor: pointer;

    &.disabled {
      background: #B3D9FF;
      cursor: not-allowed;
    }
  }
}
</style>
